import { CoreQuery, type Payout2, PayoutStatus } from "@gen/app-query";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { GridPaginationModel } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { keepPreviousData } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";

import { CopyButton } from "~/components/copy_button";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { ReadableDataGrid } from "~/components/readable_data_grid";
import { Select2, Select2TextPopover } from "~/components/select2";
import { StatusChip } from "~/components/status_chip";
import { useConfig } from "~/contexts/config";
import {
  formatPayoutDate,
  payoutStatusColors,
  payoutStatusLabels,
} from "~/features/payout_details/payoutData";
import { CurrencyFilterPopover } from "~/features/transactions/CurrencyFilterPopover";
import { DateRangePopover } from "~/features/transactions/DateRangePopover";
import { formatDateRangeForChip } from "~/features/transactions/transactionFilterUtils";
import { type CurrencyOption, useCurrencies } from "~/services/money";
import { downloadCsv, initStreamingExport } from "~/utilities/csv";
import { Money, renderMoneyWithoutCurrencySymbol } from "~/utilities/money";

import { InstantPopover } from "./InstantPopover";
import { PayoutStatusPopover } from "./PayoutStatusPopover";

const GRID_HEIGHT = 580;

const NOT_AVAILABLE = "N/A";

const EXPORT_PAYOUTS_PAGE_SIZE = 100;

const EXPORT_PAYOUTS_DEFAULT_FILE_NAME = "payouts.csv";

const CSV_HEADERS = [
  "Arrival Date",
  "Payout ID",
  "Status",
  "Destination",
  "Currency",
  "Amount",
];

function formatExportDate(date: Date, locale: string, timeZone: string) {
  return date.toLocaleDateString(locale, {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Every payout that the Core API lists has a Trustap payout ID, but the field
// is optional in the shared payout summary, so the Stripe payout ID stands in
// for it.
function getPayoutId(payout: Payout2) {
  return (
    payout.payout_summary.payout_id ?? payout.payout_summary.stripe_payout_id
  );
}

function getDestination(payout: Payout2) {
  if (!payout.last4) {
    return payout.bank_name || NOT_AVAILABLE;
  }

  return payout.bank_name
    ? `${payout.bank_name} ••••${payout.last4}`
    : `••••${payout.last4}`;
}

function getArrivalDate(payout: Payout2, formatDate: (date: Date) => string) {
  if (!payout.arrival_date) {
    return NOT_AVAILABLE;
  }

  const hasArrived = payout.status === PayoutStatus.Completed;
  return `${hasArrived ? "" : "Est. "}${formatDate(payout.arrival_date)}`;
}

export function AdminPayouts() {
  const { localization } = useConfig();
  const theme = useTheme();
  const navigate = useNavigate({ from: "/admin/payouts" });
  const search = useSearch({ from: "/admin/payouts" });
  const { enqueueSnackbar } = useSnackbar();
  const { currencies } = useCurrencies();

  const [isExporting, setIsExporting] = useState(false);

  const filters = useMemo(
    () => ({
      client_id: search.client_id,
      client_name: search.client_name,
      status: search.status,
      created_from: search.created_from,
      created_to: search.created_to,
      currency: search.currency,
      is_instant: search.is_instant,
      payout_id: search.payout_id,
      stripe_payout_id: search.stripe_payout_id,
    }),
    [search],
  );

  const { data: payoutPage, isFetching } =
    CoreQuery.useAdmin_getPayoutsByPageQuery(
      { page: search.page, page_size: search.pageSize, ...filters },
      { placeholderData: keepPreviousData },
    );

  const payouts = payoutPage?.data ?? [];
  const rowCount = payoutPage?.total_count ?? 0;

  const updateSearch = useCallback(
    (partial: Partial<typeof search>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          ...partial,
          page: 1,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleClearFilters = useCallback(() => {
    navigate({
      search: (prev) => ({
        page: 1,
        pageSize: prev.pageSize,
      }),
      replace: true,
    });
  }, [navigate]);

  const activeFilterCount = Object.values(filters).filter(
    (value) => value !== undefined,
  ).length;

  const formatDate = useMemo(
    () => (date: Date) =>
      formatPayoutDate(date, localization.locale, localization.timeZone),
    [localization.locale, localization.timeZone],
  );

  const paginationModel = useMemo(
    () => ({
      page: search.page - 1,
      pageSize: search.pageSize,
    }),
    [search.page, search.pageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: model.pageSize !== prev.pageSize ? 1 : model.page + 1,
          pageSize: model.pageSize,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const currencyChipValue = useMemo(() => {
    if (!search.currency) {
      return undefined;
    }
    return (
      currencies.find((c: CurrencyOption) => c.code === search.currency)
        ?.label ?? search.currency
    );
  }, [search.currency, currencies]);

  const setDateRange = useCallback(
    (from: Date | null, to: Date | null) =>
      updateSearch({
        created_from: from ? from.toISOString() : undefined,
        created_to: to ? to.toISOString() : undefined,
      }),
    [updateSearch],
  );

  const formatDateForChip = useCallback(
    (iso?: string) => {
      if (!iso) {
        return undefined;
      }
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) {
        return undefined;
      }
      return d.toLocaleDateString(localization.locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    },
    [localization.locale],
  );

  const instantChipValue = useMemo(() => {
    if (search.is_instant === undefined) {
      return undefined;
    }
    return search.is_instant ? "Instant" : "Standard";
  }, [search.is_instant]);

  const columns = useMemo<GridColDef<Payout2>[]>(
    () => [
      {
        field: "arrival_date",
        headerName: "Arrival Date",
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Typography variant="body2">
            {getArrivalDate(params.row, formatDate)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 140,
        flex: 1,
        sortable: false,
        renderCell: (params) =>
          params.row.status ? (
            <StatusChip
              label={payoutStatusLabels[params.row.status]}
              color={theme.palette[payoutStatusColors[params.row.status]].main}
              dataTestId="payouts-status-text"
            />
          ) : (
            <Typography variant="body2" data-testid="payouts-status-text">
              {NOT_AVAILABLE}
            </Typography>
          ),
      },
      {
        field: "payout_id",
        headerName: "Payout ID",
        minWidth: 200,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ width: "100%" }}
          >
            <Typography
              variant="body2"
              title={getPayoutId(params.row)}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getPayoutId(params.row)}
            </Typography>
            <CopyButton
              value={getPayoutId(params.row)}
              label="Copy payout ID"
              dataTestId="payouts-id-copy-button"
            />
          </Stack>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        minWidth: 140,
        flex: 1,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {new Money(
              params.row.payout_summary.amount,
              params.row.currency,
            ).format()}
          </Typography>
        ),
      },
      {
        field: "destination",
        headerName: "Destination",
        minWidth: 180,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" gap={1}>
            <PublicSvgIcon name="account_balance" size={13} />
            <Typography variant="body2">
              {getDestination(params.row)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "chevron",
        headerName: "",
        width: 44,
        sortable: false,
        align: "right",
        renderCell: () => <PublicSvgIcon name="chevron_right" size={20} />,
      },
    ],
    [formatDate, theme.palette],
  );

  const toExportRow = useCallback(
    (payout: Payout2) => [
      payout.arrival_date
        ? formatExportDate(
            payout.arrival_date,
            localization.locale,
            localization.timeZone,
          )
        : NOT_AVAILABLE,
      getPayoutId(payout),
      payout.status ? payoutStatusLabels[payout.status] : NOT_AVAILABLE,
      getDestination(payout),
      payout.currency,
      renderMoneyWithoutCurrencySymbol(
        payout.payout_summary.amount,
        payout.currency,
        localization.locale,
      ),
    ],
    [localization.locale, localization.timeZone],
  );

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    const buffer: Record<number, unknown[][]> = {};
    const nextToWriteRef = { current: 1 };
    let writable: FileSystemWritableFileStream | undefined;

    try {
      let onPageArrive: (() => Promise<void>) | undefined;
      if ("showSaveFilePicker" in window) {
        const init = await initStreamingExport(
          CSV_HEADERS,
          EXPORT_PAYOUTS_DEFAULT_FILE_NAME,
          buffer,
          nextToWriteRef,
        );
        writable = init.writable;
        onPageArrive = init.onPageArrive;
      }

      const client = CoreQuery.Client();
      let page = 1;
      let totalPages = 1;

      do {
        const result = await client.admin_getPayoutsByPage(
          page,
          EXPORT_PAYOUTS_PAGE_SIZE,
          filters.client_id,
          filters.client_name,
          filters.status,
          filters.created_from,
          filters.created_to,
          filters.currency,
          filters.is_instant,
          filters.payout_id,
          filters.stripe_payout_id,
        );
        buffer[page] = result.data.map(toExportRow);
        totalPages = result.total_pages;
        await onPageArrive?.();
        page++;
      } while (page <= totalPages);

      if (onPageArrive) {
        await onPageArrive();
        await writable?.close();
      } else {
        downloadCsv(
          [CSV_HEADERS, ...Object.values(buffer).flat()],
          EXPORT_PAYOUTS_DEFAULT_FILE_NAME,
        );
      }

      enqueueSnackbar("Successfully exported payouts", { variant: "success" });
    } catch (error) {
      const isAbortError =
        error instanceof Error && error.name === "AbortError";
      if (!isAbortError) {
        console.error("Couldn't export payouts:", error);
        enqueueSnackbar("Failed to export payouts", { variant: "error" });
      }
      try {
        await writable?.abort();
      } catch (err) {
        console.error("Couldn't abort writable file stream:", err);
      }
    } finally {
      setIsExporting(false);
    }
  }, [enqueueSnackbar, filters, toExportRow]);

  return (
    <Stack spacing={2.5}>
      <Stack
        sx={{
          backgroundColor: "primary._states.hover",
          p: { xs: 2, sm: 3, lg: 4 },
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          sx={{ columnGap: 1, rowGap: { xs: 2.5, sm: 2 } }}
        >
          <Select2
            label="Payout ID"
            iconName="account_balance"
            active={!!search.payout_id}
            value={search.payout_id}
            onClear={() => updateSearch({ payout_id: undefined })}
            dataTestId="payouts-payout-id-filter"
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={search.payout_id ?? ""}
                onApply={(v) =>
                  updateSearch({ payout_id: v.length > 0 ? v : undefined })
                }
                onClose={close}
                placeholder="Payout ID"
              />
            )}
          />
          <Select2
            label="Status"
            iconName="timeline"
            active={!!search.status}
            value={
              search.status ? payoutStatusLabels[search.status] : undefined
            }
            onClear={() => updateSearch({ status: undefined })}
            dataTestId="payouts-status-filter"
            renderPopover={(close) => (
              <PayoutStatusPopover
                initialValue={search.status}
                onApply={(status) => updateSearch({ status })}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Created"
            iconName="date"
            active={!!search.created_from || !!search.created_to}
            value={formatDateRangeForChip(
              search.created_from,
              search.created_to,
              formatDateForChip,
            )}
            onClear={() => setDateRange(null, null)}
            dataTestId="payouts-created-filter"
            renderPopover={(close) => (
              <DateRangePopover
                initialFrom={
                  search.created_from ? new Date(search.created_from) : null
                }
                initialTo={
                  search.created_to ? new Date(search.created_to) : null
                }
                onApply={setDateRange}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Client ID"
            iconName="client_id"
            active={!!search.client_id}
            value={search.client_id}
            onClear={() => updateSearch({ client_id: undefined })}
            dataTestId="payouts-client-id-filter"
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={search.client_id ?? ""}
                onApply={(v) =>
                  updateSearch({ client_id: v.length > 0 ? v : undefined })
                }
                onClose={close}
                placeholder="Client ID"
              />
            )}
          />
          <Select2
            label="Client Name"
            iconName="client_storefront"
            active={!!search.client_name}
            value={search.client_name}
            onClear={() => updateSearch({ client_name: undefined })}
            dataTestId="payouts-client-name-filter"
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={search.client_name ?? ""}
                onApply={(v) =>
                  updateSearch({ client_name: v.length > 0 ? v : undefined })
                }
                onClose={close}
                placeholder="Client Name"
              />
            )}
          />
          <Select2
            label="Currency"
            iconName="currency"
            active={!!search.currency}
            value={currencyChipValue}
            onClear={() => updateSearch({ currency: undefined })}
            dataTestId="payouts-currency-filter"
            renderPopover={(close) => (
              <CurrencyFilterPopover
                currencies={currencies}
                initialValue={search.currency ?? ""}
                onApply={(code) =>
                  updateSearch({ currency: code || undefined })
                }
                onClose={close}
              />
            )}
          />
          <Select2
            label="Speed"
            iconName="sync"
            active={search.is_instant !== undefined}
            value={instantChipValue}
            onClear={() => updateSearch({ is_instant: undefined })}
            dataTestId="payouts-instant-filter"
            renderPopover={(close) => (
              <InstantPopover
                initialValue={search.is_instant}
                onApply={(isInstant) => updateSearch({ is_instant: isInstant })}
                onClose={close}
              />
            )}
          />
          <Select2
            label="Stripe Payout ID"
            iconName="swap_horiz"
            active={!!search.stripe_payout_id}
            value={search.stripe_payout_id}
            onClear={() => updateSearch({ stripe_payout_id: undefined })}
            dataTestId="payouts-stripe-payout-id-filter"
            renderPopover={(close) => (
              <Select2TextPopover
                currentValue={search.stripe_payout_id ?? ""}
                onApply={(v) =>
                  updateSearch({
                    stripe_payout_id: v.length > 0 ? v : undefined,
                  })
                }
                onClose={close}
                placeholder="Stripe Payout ID"
              />
            )}
          />
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
          >
            Clear all
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="text"
          onClick={() => {
            void handleExport();
          }}
          disabled={rowCount === 0 || isExporting}
          data-testid="payouts-export-button"
          sx={{ px: 1, py: 0.75, borderRadius: 1 }}
          startIcon={
            <PublicSvgIcon
              name="ios_share"
              inheritColor
              sx={{ "& svg": { width: 12, height: 16 } }}
            />
          }
        >
          {isExporting ? "Exporting…" : "Export"}
        </Button>
      </Stack>

      <Box
        sx={{
          height: GRID_HEIGHT,
          backgroundColor: "common.white",
          borderRadius: 1.5,
        }}
      >
        <ReadableDataGrid<Payout2>
          dataTestId="payouts-grid"
          storageKey="AdminPayouts"
          columns={columns}
          rows={payouts}
          rowCount={rowCount}
          getRowId={getPayoutId}
          loading={isFetching}
          hideToolbar
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          rowNavigation={{
            getUrl: (row: Payout2) => `/admin/payouts/${getPayoutId(row)}`,
            onNavigate: (url: string) => navigate({ to: url }),
          }}
          localeText={{
            noRowsLabel: "No Payouts",
          }}
        />
      </Box>
    </Stack>
  );
}
